import { useState, useEffect, useMemo, useRef } from "react";

type UseQRCodeSizeOptions = {
  maxSize: number;
  minSize: number;
  padding?: number;
};

type UseQRCodeSizeReturn = {
  containerRef: React.RefObject<HTMLDivElement>;
  qrCodeSize: number;
};

export const useQRCodeSize = ({
  maxSize,
  minSize,
  padding = 0,
}: UseQRCodeSizeOptions): UseQRCodeSizeReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(500);

  useEffect(() => {
    let mounted = true;

    const updateContainerWidth = () => {
      if (mounted && containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);

    return () => {
      mounted = false;
      window.removeEventListener("resize", updateContainerWidth);
    };
  }, []);

  const qrCodeSize = useMemo(() => {
    const availableWidth = containerWidth - padding;
    return Math.max(minSize, Math.min(maxSize, availableWidth));
  }, [containerWidth, padding, maxSize, minSize]);

  return {
    containerRef,
    qrCodeSize,
  };
};
